import React, { useEffect, useState } from 'react'
import { useLocalStorage, useTrans } from 'hooks'
import { Page } from 'components/organisms'
import styles from './RegistrazioneTemplate.module.sass'
import { Form, Formik } from 'formik'
import { PaginaNode, useCreateUtenteMutation } from '@gql/graphql'
import {
  Button,
  FormError,
  FormField,
  FormikField,
  FormLayout,
  FormText,
  WContainer,
} from '@components/atoms'
import { registrazioneValidation } from '@utils/validationSchemas'
import { useRouter } from 'next/router'
import { handleSignUpCompleteTracking } from '@utils/tracking'

interface Props {
  pagina?: PaginaNode
}

const RegistrazioneTemplate = (props: Props) => {
  const { pagina = undefined } = props

  const t = useTrans()
  const [signIn, { loading: signInLoading }] = useCreateUtenteMutation()
  const tipoUtenteOpts = [
    { label: t('Privato'), value: 'privato' },
    { label: t('Azienda'), value: 'azienda' },
  ]

  const [emailSignIn, setEmailSignIn] = useLocalStorage('email_signin', null)
  const [error, setError] = useState('')
  const router = useRouter()

  const initialValues = {
    email: emailSignIn || '',
    tipoUtente: tipoUtenteOpts[0].value,
    nome: '',
    cognome: '',
    cf: '',
    ragioneSociale: '',
    piva: '',
    sdi: '',
    noSdi: false,
    noCodiceSdi: false,
    pec: '',
    password: '',
    privacy: false,
    consensoMkt: false,
  }

  useEffect(() => {
    setEmailSignIn(null)
  }, [])

  const handleSignIn = async (values) => {
    const { data } = await signIn({
      variables: {
        input: {
          email: values.email,
          privato: values.tipoUtente === 'privato',
          nome: values.nome,
          cognome: values.cognome,
          ragioneSociale: values.tipoUtente === 'azienda' ? values.ragioneSociale : null,
          cf: values.cf,
          piva: values.tipoUtente === 'azienda' ? values.piva : null,
          sdi: values.tipoUtente === 'azienda' ? values.sdi : null,
          pec: values.tipoUtente === 'azienda' ? values.pec : null,
          password: values.password,
          consensoMkt: values.consensoMkt,
          guest: false,
        },
      },
    })

    if (data?.createUtente?.status) {
      await handleSignUpCompleteTracking(values.tipoUtente, 'complete')
      router.push(`/sign-in/complete${window.location.search}`)
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setError(t(data?.createUtente?.message) || t('Errore durante la creazione'))
      await handleSignUpCompleteTracking(values.tipoUtente, 'error')
    }
  }

  return (
    <Page pagina={pagina} className={`${styles.root}`}>
      <main>
        <WContainer>
          <div className={styles.layout}>
            <h1 className={styles.title}>{pagina.titolo}</h1>
            <Formik
              initialValues={initialValues}
              onSubmit={(values) => handleSignIn(values)}
              validationSchema={registrazioneValidation(t, true)}
            >
              {(formikProps) => {
                const { errors, isSubmitting } = formikProps

                useEffect(() => {
                  if (isSubmitting && Object.keys(errors).length > 0) {
                    const firstErrorField = Object.keys(errors)[0]
                    const errorElement = document.getElementById(`registrazione_${firstErrorField}`)

                    if (errorElement) {
                      errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                  }
                }, [isSubmitting, errors])

                return (
                  <Form className={styles.form}>
                    <FormError
                      id="profile_global_error"
                      message={error}
                      visible={!!error}
                      variant="global"
                    />
                    <FormLayout space="sm">
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_email'}
                        name="email"
                        type="email"
                        label={t('Indirizzo email')}
                        placeholder={t('Inserisci il tuo indirizzo email')}
                        required
                        layout="full"
                      />
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_tipo_utente'}
                        name="tipoUtente"
                        type="radio"
                        variant="horizontal"
                        options={tipoUtenteOpts}
                        required
                        layout="full"
                      />
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_nome'}
                        name="nome"
                        type="text"
                        label={t('Nome')}
                        placeholder={t('Inserisci il tuo nome')}
                        required
                      />
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_cognome'}
                        name="cognome"
                        type="text"
                        label={t('Cognome')}
                        placeholder={t('Inserisci il tuo cognome')}
                        required
                      />
                      {formikProps.values.tipoUtente === 'azienda' && (
                        <FormikField
                          formik={formikProps}
                          id={'registrazione_ragione_sociale'}
                          name="ragioneSociale"
                          type="text"
                          label={t('Ragione sociale')}
                          placeholder={t('Inserisci la tua ragione sociale')}
                          required
                          layout="full"
                        />
                      )}

                      <FormField
                        id={'registrazione_cf'}
                        label={t('Codice fiscale')}
                        required={!formikProps.values.noSdi}
                        childrenLayout="column-sm"
                        className={styles.sdiField}
                        layout="full"
                      >
                        <FormText
                          id={'registrazione_cf'}
                          name="cf"
                          type="text"
                          size="lg"
                          placeholder={t('Inserisci il tuo codice fiscale')}
                          required
                          onChange={formikProps.handleChange}
                          onBlur={formikProps.handleBlur}
                          status={
                            (formikProps.touched?.['cf'] &&
                              formikProps.values.tipoUtente === 'privato') ||
                            formikProps.touched?.['piva'] ||
                            (!formikProps.errors?.['piva'] && formikProps.values?.['piva'])
                              ? formikProps.errors?.['cf']
                                ? 'error'
                                : 'success'
                              : ''
                          }
                          value={formikProps.values.cf}
                          errorMessage={formikProps.errors?.['cf']}
                        />
                      </FormField>

                      {formikProps.values.tipoUtente === 'azienda' && (
                        <>
                          <FormikField
                            formik={formikProps}
                            id={'registrazione_piva'}
                            name="piva"
                            type="text"
                            label={t('Partita IVA')}
                            placeholder={t('Inserisci il tuo numero di partita IVA')}
                            required
                            layout="full"
                          />

                          <FormikField
                            formik={formikProps}
                            id={'registrazione_sdi'}
                            name="sdi"
                            type="text"
                            label={t('Codice SDI')}
                            placeholder={t('Inserisci il tuo codice SDI')}
                            required={!formikProps.values.noSdi}
                            disabled={formikProps.values.noSdi}
                            layout="full"
                          />
                          <FormikField
                            formik={formikProps}
                            id={'registrazione_nosdi'}
                            name="noSdi"
                            type="checkbox"
                            checkboxLabel={t('Non ho il codice SDI')}
                            layout="full"
                          />
                          <FormikField
                            formik={formikProps}
                            id={'registrazione_pec'}
                            name="pec"
                            type="email"
                            label={t('PEC')}
                            placeholder={t('Inserisci la tua PEC')}
                            required={formikProps.values.noSdi}
                            layout="full"
                          />
                        </>
                      )}
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_password'}
                        name="password"
                        type="password"
                        label={t('Password')}
                        placeholder={t('Inserisci la tua password')}
                        required
                        layout="full"
                        helper={t('La password deve essere almeno di 8 caratteri')}
                      />
                    </FormLayout>
                    <div
                      className={styles.privacy}
                      dangerouslySetInnerHTML={{
                        __html: t(
                          "Ti invitiamo a visionare <a href='/privacy/informativa-per-marketing-e-profilazione/'>l'informativa privacy art. 13 GDPR</a> sul trattamento dei dati"
                        ),
                      }}
                    />
                    <FormLayout space="sm">
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_privacy'}
                        name="privacy"
                        type="checkbox"
                        label={t('Letta e compresa l’informativa*')}
                        checkboxLabel={t(
                          'Esprimo il consenso al trattamento dei miei dati per ricevere supporto, risposte ai miei quesiti e per le eventuali attività connesse di cui al punto a) dell’informativa.'
                        )}
                        fieldVariant="box"
                        layout="full"
                      />
                      <FormikField
                        formik={formikProps}
                        id={'registrazione_marketing'}
                        name="consensoMkt"
                        type="checkbox"
                        label={t('Consenso per il ‘Marketing diretto’ (facoltativo)')}
                        checkboxLabel={t(
                          'Esprimo il consenso per le finalità di cui al punto b) dell’informativa e quindi acconsento al ricevimento di comunicazioni pubblicitarie tramite e-mail, telefono, sms e similari. Resta inteso che potrò revocare in qualunque momento il presente consenso.'
                        )}
                        fieldVariant="box"
                        layout="full"
                      />
                    </FormLayout>
                    <Button
                      type="submit"
                      label={t('Registrati')}
                      loading={signInLoading}
                      size="lg"
                      className={styles.cta}
                    />
                  </Form>
                )
              }}
            </Formik>
            <div className={styles.requirements}>* {t('Campi obbligatori')}</div>
          </div>
        </WContainer>
      </main>
    </Page>
  )
}

export default RegistrazioneTemplate
